import { Metamodel } from '../base/model';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { ActionConstants } from '../../constants/action-constants';
import { TaggableNamed } from '../base/taggable-named';
import { Collection, IModel, IViolation } from '../base/annotation';
import { TriggerPipeline } from './trigger-pipeline';
import { TriggerProperties } from './trigger-properties';
import { sanitizeAndFormatString } from '../../shared/utils/string.util';
import { Pipeline } from '../pipeline/pipeline';
import { ParameterValue } from '../base/parameter-value';
import { Owned } from '../base/named';
import { TriggerType } from './trigger-constants';
import { ScheduleTriggerProperties, TriggerFrequency } from './schedule-trigger-properties';
import { RecurrenceSchedule } from './recurrence-schedule';
import { RetryPolicy } from './tumbling-window-retry-policy';
import { EventTriggerProperties, TriggerEvent } from './event-trigger-properties';
import { ChainingTriggerProperties } from './chaining-trigger-properties';
import { CustomEventsTriggerProperties } from './custom-events-trigger-properties';
import { TriggerDependency } from './trigger-dependency';
import { TumblingWindowTriggerProperties } from './tumbling-window-trigger-properties';
import { RerunTumblingWindowTriggerProperties } from './rerun-tumbling-window-trigger-properties';
import { IAdvancedFilter } from '../../interfaces/IAdvancedFilter';
import { ModelConstants } from '../model-constants';

@Metamodel.model({
    name: ModelConstants.trigger,
    display: [shellStringResources.Trigger, shellStringResources.Triggers],
    referable: true,
    reusable: true,
    constraints: {
        name: {
            required: true,
            pattern: new RegExp('^[^-\\s][^\\\\/:*?"<>|]{1,140}$')
        }
    },
    accessPublishConstraint: {
        writeResource: {
            action: ActionConstants.triggersWrite
        },
        deleteResource: {
            action: ActionConstants.triggersDelete
        }
    }
})
export class Trigger extends TaggableNamed {

    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        type: TriggerPipeline.MODEL,
        collection: Collection.Array,
        constraint: {
            minOccurs: 0,
            unique: true,
            validator: Trigger.validatePipelines
        }
    })
    public readonly pipelines: TriggerPipeline[] = [];

    @Metamodel.attribute({
        type: TriggerProperties.MODEL
    })
    public properties: TriggerProperties;

    @Metamodel.attribute({
        display: shellStringResources.Activated,
        constraint: {
            required: true
        }
    })
    public isActivated: boolean = false;

    public unknownType: boolean = false;

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    private static validatePipelines(trigger?: Trigger, pipelines?: TriggerPipeline[]): IViolation[] {
        const violations: IViolation[] = [];
        if (trigger && pipelines) {
            if (trigger.isActivated && pipelines.length === 0) {
                violations.push({
                    message: () => sanitizeAndFormatString(shellStringResources.Trigger_ActivationErrorNoPipeline, trigger.name)
                });
            }
            if (!trigger.properties.multiplePipelines && pipelines.length > 1) {
                violations.push({
                    message: () => sanitizeAndFormatString(shellStringResources.Trigger_PipelinesLengthError, trigger.name)
                });
            }
        }
        return violations;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public addPipeline(pipeline: Pipeline, parameters?: ParameterValue[]): Trigger {
        const triggerPipeline = Owned.create<TriggerPipeline>(TriggerPipeline.MODEL, this);
        triggerPipeline.pipeline = pipeline;
        if (parameters) {
            parameters.forEach((p) => triggerPipeline.parameters.push(p));
        }
        this.pipelines.push(triggerPipeline);
        return this;
    }

    public removePipeline(pipeline: Pipeline): Trigger {
        const index = this.pipelines.findIndex((p) => p.pipeline === pipeline);
        if (index > -1) {
            this.pipelines.splice(index, 1);
        }
        return this;
    }

    public getTriggerPipeline(pipeline: Pipeline): TriggerPipeline | undefined {
        return this.pipelines.find((p) => pipeline && p.pipeline && p.pipeline.name === pipeline.name);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public updatePipelineParameters(pipeline: Pipeline, parameters: ParameterValue[]): void {
        const existingPipeline = this.getTriggerPipeline(pipeline);
        if (existingPipeline) {
            existingPipeline.parameters.length = 0;
            parameters.forEach((p) => existingPipeline.parameters.push(p));
        }
    }

    public getType(): TriggerType {
        return this.properties ? (Metamodel.modelOf(this.properties).name as TriggerType) : 'Unknown';
    }

    public createScheduledTrigger(frequency: TriggerFrequency, startTime: Date, timeZone: string, interval?: number, endTime?: Date, schedule?: RecurrenceSchedule): void {
        const scheduleProperties = Owned.create<ScheduleTriggerProperties>(ScheduleTriggerProperties.MODEL, this);
        scheduleProperties.setRecurrence(frequency, startTime, timeZone, interval, endTime, schedule);
        this.properties = scheduleProperties;
    }

    public createTumblingWindowTrigger(frequency: TriggerFrequency, startTime: Date, interval: number, delay: string,
         maxConcurrency: number, endTime?: Date, retryPolicy?: RetryPolicy, dependsOn?: TriggerDependency[]): void {
        const tumblingWindowProperties = Owned.create<TumblingWindowTriggerProperties>(TumblingWindowTriggerProperties.MODEL, this);
        tumblingWindowProperties.setRecurrence(frequency, startTime, interval, delay, maxConcurrency, endTime, retryPolicy);
        tumblingWindowProperties.dependsOn = dependsOn || [];
        this.properties = tumblingWindowProperties;
    }

    public createRerunRangeTumblingWindowTrigger(parentTriggerName: string, startTime: Date, endTime: Date, rerunConcurrency: number): void {
       const rerunTumblingWindowProperties = Owned.create<RerunTumblingWindowTriggerProperties>(RerunTumblingWindowTriggerProperties.MODEL, this);
       rerunTumblingWindowProperties.parentTrigger = parentTriggerName;
       rerunTumblingWindowProperties.requestedStartTime = startTime;
       rerunTumblingWindowProperties.requestedEndTime = endTime;
       rerunTumblingWindowProperties.rerunConcurrency = rerunConcurrency;
       this.properties = rerunTumblingWindowProperties;
    }

    public createBlobEventsTrigger(
            scope: string, tenantId: string, events: TriggerEvent[], blobPathBeginsWith?: string, blobPathEndsWith?: string, ignoreEmptyBlobs?: boolean, maxConcurrency?: number): void  {
        const blobEventsTriggerProperties = Owned.create<EventTriggerProperties>(EventTriggerProperties.MODEL, this);
        blobEventsTriggerProperties.scope = scope;
        blobEventsTriggerProperties.tenantId = tenantId;
        blobEventsTriggerProperties.blobPathBeginsWith = blobPathBeginsWith;
        blobEventsTriggerProperties.blobPathEndsWith = blobPathEndsWith;
        blobEventsTriggerProperties.events = events || [];
        blobEventsTriggerProperties.ignoreEmptyBlobs = ignoreEmptyBlobs;
        blobEventsTriggerProperties.maxConcurrency = maxConcurrency;
        this.properties = blobEventsTriggerProperties;
    }

    public createChainingTrigger(dependsOn: Pipeline[], runDimension: string): void { // todo fix any
        const chainingTriggerProperties = Owned.create<ChainingTriggerProperties>(ChainingTriggerProperties.MODEL, this);
        chainingTriggerProperties.dependsOn = dependsOn;
        chainingTriggerProperties.runDimension = runDimension;
        this.properties = chainingTriggerProperties;
    }

    public createCustomEventsTrigger(scope: string, tenantId: string, events: string[], subjectBeginsWith?: string, subjectEndsWith?: string, advancedFilters?: IAdvancedFilter[]): void  {
        const eventsTriggerProperties = Owned.create<CustomEventsTriggerProperties>(CustomEventsTriggerProperties.MODEL, this);
        eventsTriggerProperties.scope = scope;
        eventsTriggerProperties.tenantId = tenantId;
        eventsTriggerProperties.subjectBeginsWith = subjectBeginsWith;
        eventsTriggerProperties.subjectEndsWith = subjectEndsWith;
        eventsTriggerProperties.events = events || [];
        eventsTriggerProperties.advancedFilters = advancedFilters || [];
        this.properties = eventsTriggerProperties;
    }
}
